import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Mail, Phone, Heart, GraduationCap, Code, Users, Award } from "lucide-react";
import styles from "./AboutUs.module.css";
import Footer from "./Footer";

const AboutUs = () => {
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        }
    };

    return (
        <div className={styles.page}>
            {/* Hero Section */}
            <motion.header 
                className={styles.hero}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
            >
                <div className={styles.heroIcon}>
                    <GraduationCap size={48} />
                </div>
                <h1>About This Portal</h1>
                <p>A student-built initiative to make campus life easier</p>
            </motion.header>

            <motion.main 
                className={styles.content}
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                {/* Story Section */}
                <motion.section 
                    className={styles.section}
                    variants={itemVariants}
                    whileHover={{ scale: 1.02 }}
                >
                    <div className={styles.sectionIcon}>
                        <Code size={24} />
                    </div>
                    <h2>My Journey</h2>
                    <p>
                        Hi, I'm <strong>Zeeshan Sarfraz</strong>, a Computer Science major and
                        the creator of this portal. The idea came from a simple frustration:
                        our university portal wasn't functional at all, and almost nothing
                        was automated.
                    </p>
                    <p>
                        As a CS student, I wanted to solve problems that directly affect
                        students like us. That's when I decided to build this portal,
                        starting small, but always aiming to make things better and more
                        accessible.
                    </p>
                </motion.section>

                {/* Purpose Section */}
                <motion.section 
                    className={styles.section}
                    variants={itemVariants}
                    whileHover={{ scale: 1.02 }}
                >
                    <div className={styles.sectionIcon}>
                        <Award size={24} />
                    </div>
                    <h2>Why I Built It</h2>
                    <p>
                        I wanted to bring automation to areas where students struggle the
                        most: managing results, keeping track of homework, timetables, and
                        academic tools like grade calculators. This portal is my attempt to
                        make student life smoother, faster, and more reliable.
                    </p>
                </motion.section>

                {/* Gratitude Section */}
                <motion.section 
                    className={styles.section}
                    variants={itemVariants}
                    whileHover={{ scale: 1.02 }}
                >
                    <div className={styles.sectionIcon}>
                        <Users size={24} />
                    </div>
                    <h2>Special Thanks</h2>
                    <p>
                        This project wasn't built alone. I'm thankful to my friends and one
                        of my teachers who helped me in many ways.
                        Some gave me technical support, while others encouraged me with
                        their words, reminding me that I can make this possible.
                    </p>
                    <p>
                        Their support kept me going, and this portal wouldn't exist without
                        them.
                    </p>
                </motion.section>

                {/* Closing Section */}
                <motion.section 
                    className={styles.section + " " + styles.closing}
                    variants={itemVariants}
                    whileHover={{ scale: 1.02 }}
                >
                    <div className={styles.heartWrapper}>
                        <Heart size={32} className={styles.heartIcon} />
                    </div>
                    <h2>A Student Effort for Students</h2>
                    <p>
                        This portal is far from perfect, but it's a step toward something
                        better. I'll keep improving it, adding new features, and making it
                        more useful for everyone.
                        Thank you for being part of this journey.
                    </p>
                </motion.section>
            </motion.main>
            
            <Footer />
        </div>
    );
};

export default AboutUs;
